import EmailResubscribeModal, { type EmailResubscribeModalResult } from 'components/Account/EmailResubscribeModal'
import { trackLoyaltyActionRewardCTA } from 'analytics/snowplow/events/loyaltyActionRewardCTAEvents'
import Clickable from 'components/Common/Clickable'
import ModalContext from 'contexts/ModalContext'
import {
  type SupportedLoyaltyActionReward,
  SupportedActionReward,
  type ComponentRenderActionReward,
  type LoyaltyActionRewardCTANav,
  isComponentRenderActionReward,
} from 'loyalty/constants/loyaltyActionRewards'
import { useAppSelector } from 'hooks/reduxHooks'
import useNavigateTo from 'hooks/useNavigateTo'
import { useWhatsAppChannelUrl } from 'hooks/useWhatsAppChannelUrl'
import { isAndroid, isAndroidAppUserAgent, isApple, isIOSAppUserAgent } from 'lib/web/deviceUtils'
import { useCallback, useContext, type ComponentType, type PropsWithChildren } from 'react'
import styled from 'styled-components'
import LoyaltyActionRewardContext from './LoyaltyActionRewardContext'

const LUXURY_ESCAPES_APP_SMART_LINK_IOS = 'https://app.luxuryescapes.com/3WHB/1aab092'
const LUXURY_ESCAPES_APP_SMART_LINK_ANDROID = 'https://app.luxuryescapes.com/3WHB/feb04c69'

const InteractiveListItemRow = styled(Clickable)`
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: inherit;
  text-decoration: none;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }
`

const COMPONENT_RENDER_BY_ACTION: Record<ComponentRenderActionReward, ComponentType> = {
  [SupportedActionReward.SUBSCRIBE_TO_COMMUNICATIONS]: EmailResubscribeModal,
}

function getLuxuryEscapesAppSmartLink(osName: string, userAgent: string): string {
  if (isAndroidAppUserAgent(userAgent) || isAndroid(osName)) {
    return LUXURY_ESCAPES_APP_SMART_LINK_ANDROID
  }

  if (isIOSAppUserAgent(userAgent) || isApple(osName)) {
    return LUXURY_ESCAPES_APP_SMART_LINK_IOS
  }

  return LUXURY_ESCAPES_APP_SMART_LINK_ANDROID
}

interface Props extends PropsWithChildren {
  reward: SupportedLoyaltyActionReward
  ctaNav?: LoyaltyActionRewardCTANav
  itemPosition: number
}

function LoyaltyActionRewardCTA({ reward, children, ctaNav, itemPosition }: Props) {
  const navigateTo = useNavigateTo()
  const showModal = useContext(ModalContext)
  const { startPolling } = useContext(LoyaltyActionRewardContext)

  const osName = useAppSelector(state => state.system.device.osName)
  const userAgent = useAppSelector(state => state.system.rawUserAgentString)

  const ComponentToRender = ctaNav?.kind === 'component-render' && isComponentRenderActionReward(reward.actionName) ?
    COMPONENT_RENDER_BY_ACTION[reward.actionName] :
    undefined

  const appSmartLink = getLuxuryEscapesAppSmartLink(osName, userAgent)
  const whatsAppChannelUrl = useWhatsAppChannelUrl()
  const handleRouteActivate = useCallback(() => {
    if (ctaNav?.kind !== 'route') return

    trackLoyaltyActionRewardCTA(
      reward,
      { destinationUrl: ctaNav.to },
      itemPosition,
    )
    navigateTo(ctaNav.to)
  }, [ctaNav, itemPosition, navigateTo, reward])

  const handleAppSmartLinkActivate = useCallback(() => {
    trackLoyaltyActionRewardCTA(
      reward,
      { destinationUrl: appSmartLink },
      itemPosition,
    )
  }, [appSmartLink, itemPosition, reward])

  const handleWhatsAppChannelActivate = useCallback(() => {
    trackLoyaltyActionRewardCTA(
      reward,
      { destinationUrl: whatsAppChannelUrl },
      itemPosition,
    )
  }, [itemPosition, reward, whatsAppChannelUrl])

  const handleComponentRenderActivate = useCallback(async() => {
    if (!ComponentToRender) return

    trackLoyaltyActionRewardCTA(
      reward,
      {},
      itemPosition,
    )
    const result = await showModal<EmailResubscribeModalResult>(<ComponentToRender />)
    if (result?.submitted) {
      startPolling(reward.actionName)
    }
  }, [ComponentToRender, itemPosition, reward, showModal, startPolling])

  if (!ctaNav) return children

  if (ctaNav.kind === 'component-render' && ComponentToRender) {
    return <InteractiveListItemRow type="button" onClick={handleComponentRenderActivate}>
      {children}
    </InteractiveListItemRow>
  }

  if (ctaNav.kind === 'app-smart-link') {
    return <InteractiveListItemRow
      href={appSmartLink}
      target="_blank"
      rel="noopener"
      onClick={handleAppSmartLinkActivate}
    >
      {children}
    </InteractiveListItemRow>
  }

  if (ctaNav.kind === 'whatsapp-channel' && whatsAppChannelUrl) {
    return <InteractiveListItemRow
      href={whatsAppChannelUrl}
      target="_blank"
      rel="noopener"
      onClick={handleWhatsAppChannelActivate}
    >
      {children}
    </InteractiveListItemRow>
  }

  if (ctaNav.kind === 'route') {
    return <InteractiveListItemRow type="button" onClick={handleRouteActivate}>
      {children}
    </InteractiveListItemRow>
  }

  return children
}

export default LoyaltyActionRewardCTA
